import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';

type FacebookPixelContextType = {
  trackFacebookEvent: (eventName: string, params?: Record<string, any>) => void;
};

const FacebookPixelContext = createContext<FacebookPixelContextType>({
  trackFacebookEvent: () => {},
});

export const FacebookPixelProvider = ({ children }: { children: React.ReactNode }) => {
  const pixelId = process.env.FB_PIXEL_ID || '';
  const router = useRouter();
  const initialized = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !pixelId || process.env.NODE_ENV === 'development') return;

    if (!window.fbq) {
      // @ts-ignore
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

      window.fbq('init', pixelId);
      initialized.current = true;
    }

    try {
      window.fbq('track', 'PageView');
    } catch (error) {
      console.error('Error tracking pixel event:', error);
    }

    const unsubscribe = router.subscribe('onBeforeLoad', () => {
      try {
        window.fbq('track', 'PageView');
      } catch (error) {
        console.error('Error tracking pixel event:', error);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [pixelId, router]);

  const trackFacebookEvent = (eventName: string, params = {}) => {
    try {
      if (typeof window === 'undefined' || !pixelId || process.env.NODE_ENV === 'development') {
        return;
      }
      window.fbq('track', eventName, params);
    } catch (error) {
      console.error('Error tracking pixel event:', error);
    }
  };

  return (
    <FacebookPixelContext.Provider value={{ trackFacebookEvent }}>
      {children}
    </FacebookPixelContext.Provider>
  );
};

export const useFacebookPixel = () => useContext(FacebookPixelContext);

export const sha256 = async (input: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(input.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

declare global {
  interface Window {
    fbq: (type: string, eventName: string, params?: Record<string, any>) => void;
    _fbq: any;
  }
}
